// numericFieldSync.ts
import type { Editor } from '@tiptap/core';

/**
 * Read and rewrite the numeric report slots inside an open report.
 *
 * WHY THIS IS NEEDED
 * `handleCommitNumeric` writes a typed value straight back into the span the
 * clinician clicked, and stops there. That is fine for a value that appears once
 * and derives nothing. It is not enough for the stress heart rates, where
 * Dr Shashi asked for the percentage to follow the typed peak HR "on both
 * conclusion and body" — two occurrences of the SAME key, in different sentences,
 * one of which the clinician never clicked.
 *
 * WHY NOT REUSE THE MEASUREMENT CHAIN
 * The echo measurement chain (`chainField`) solves a similar problem, but its
 * marks are stripped before a report is stored and rebuilt on open by anchoring
 * to a `Label:` cell in the measurement table. Stress values live in free prose —
 * "Heart rate increased from 72 BPM to 158 BPM (92%)" — with no label to anchor
 * to, so that rebuild would never find them. The `editableNumber` mark, by
 * contrast, is PERSISTED in the saved document, so the identity is already
 * durable here and needs no backfill. Two mechanisms, each suited to its own
 * document shape.
 *
 * The key is the span's FIRST class, exactly as `wrapNumericField` emits it and
 * as the click handler reads it back. Nothing new is invented.
 */

const NUMERIC_MARK = 'editableNumber';

/** First class token of a numeric mark, i.e. the placeholder key it stands for. */
const keyForMarks = (marks: readonly any[] | undefined): string | null => {
  if (!marks) {
    return null;
  }
  const mark = marks.find(m => m?.type?.name === NUMERIC_MARK);
  const className = mark?.attrs?.class;
  if (typeof className !== 'string') {
    return null;
  }
  const first = className.trim().split(/\s+/)[0];
  return first || null;
};

type NumericRange = {
  key: string;
  from: number;
  to: number;
  text: string;
  marks: readonly any[];
};

/**
 * Every numeric slot in the document, in document order.
 *
 * Adjacent text nodes carrying the same key are merged into one range. TipTap
 * splits a text node whenever an unrelated attribute changes, so "158" can
 * arrive as "15" + "8" after an edit; replacing those separately would leave a
 * stale fragment behind.
 */
function collectNumericRanges(editor: Editor): NumericRange[] {
  const ranges: NumericRange[] = [];
  if (!editor?.state?.doc) {
    return ranges;
  }

  editor.state.doc.descendants((node: any, pos: number) => {
    if (!node.isText) {
      return true;
    }
    const key = keyForMarks(node.marks);
    if (!key) {
      return true;
    }

    const previous = ranges[ranges.length - 1];
    if (previous && previous.key === key && previous.to === pos) {
      previous.to = pos + node.nodeSize;
      previous.text += node.text || '';
      return true;
    }

    ranges.push({
      key,
      from: pos,
      to: pos + node.nodeSize,
      text: node.text || '',
      marks: node.marks,
    });
    return true;
  });

  return ranges;
}

/**
 * Current value of every numeric slot, keyed by placeholder.
 *
 * A key printed more than once collapses to its LAST occurrence. That is the
 * right default here: all occurrences of one key are kept identical by
 * `applyNumericUpdates`, so they cannot legitimately disagree, and if a manual
 * edit ever made them disagree the later one is the one the clinician saw most
 * recently.
 */
export function collectNumericSnapshot(editor: Editor): Record<string, string> {
  const snapshot: Record<string, string> = {};
  collectNumericRanges(editor).forEach(range => {
    snapshot[range.key] = range.text.trim();
  });
  return snapshot;
}

/**
 * Write new values into every occurrence of their key.
 *
 * Returns the number of occurrences actually rewritten.
 *
 * Deliberate choices:
 * - **One transaction.** A separate transaction per occurrence would let the
 *   editor's `update` handler fire mid-way and autosave a half-updated report.
 * - **Applied last-to-first.** Replacing text changes every position after it;
 *   walking backwards keeps the ranges collected up front valid.
 * - **Marks are carried over explicitly.** `insertText` at a replaced range
 *   would drop the `editableNumber` mark and the slot would stop being
 *   clickable — the value would be right and permanently uncorrectable.
 * - **Unchanged values are skipped**, so a no-op derivation does not dirty the
 *   document and trigger an autosave of an unmodified report.
 * - **Empty values are ignored.** Clearing a slot is what the `--` marker is
 *   for; writing an empty string would collapse the span and lose the field.
 */
export function applyNumericUpdates(
  editor: Editor,
  updates: Record<string, string>
): number {
  if (!editor || !updates || Object.keys(updates).length === 0) {
    return 0;
  }

  const targets = collectNumericRanges(editor).filter(range => {
    const next = updates[range.key];
    return typeof next === 'string' && next.trim() !== '' && next.trim() !== range.text.trim();
  });

  if (targets.length === 0) {
    return 0;
  }

  const { tr } = editor.state;
  // Housekeeping the clinician did not perform — an undo should take back their
  // own edit, not this follow-on write.
  tr.setMeta('addToHistory', false);

  for (let i = targets.length - 1; i >= 0; i -= 1) {
    const range = targets[i];
    const text = updates[range.key].trim();
    tr.replaceWith(range.from, range.to, editor.schema.text(text, range.marks as any));
  }

  editor.view.dispatch(tr);
  return targets.length;
}
